'use client';

import { FC } from 'react';
import type { Content } from '@prismicio/client';
import { isFilled } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { OutlineCtaLink } from '@/components/common/OutlineCtaLink';
import { PrimaryGreenCtaButton } from '@/components/common/PrimaryGreenCtaButton';
import { RevealOnScroll } from '@/components/common/RevealOnScroll';
import { useIsMobile } from '@/hooks';
import { cn } from '@/lib/utils';
import {
  CalloutBody,
  CalloutCardShell,
  CalloutLogo,
  CalloutTitle,
  CtaEqualWidthRow,
} from './components';

export type SplitParagraphCalloutProps =
  SliceComponentProps<Content.SplitParagraphCalloutSlice>;

type DefaultSlice = Extract<
  Content.SplitParagraphCalloutSlice,
  { variation: 'default' }
>;

type TitleWithCtasSlice = Extract<
  Content.SplitParagraphCalloutSlice,
  { variation: 'titleWithCtas' }
>;

type LogoDescriptionCtaSlice = Extract<
  Content.SplitParagraphCalloutSlice,
  { variation: 'logoDescriptionCta' }
>;

const SplitParagraphCalloutDefault: FC<SliceComponentProps<DefaultSlice>> = ({
  slice,
}) => {
  const { title, body, background_image, remove_upper_margin } = slice.primary;

  const hasTitle = isFilled.richText(title);
  const hasBody = isFilled.richText(body);
  const hasBackgroundImage = isFilled.image(background_image);

  if (!hasTitle && !hasBody && !hasBackgroundImage) {
    return null;
  }

  return (
    <CalloutCardShell
      sliceType={slice.slice_type}
      variation={slice.variation}
      removeUpperMargin={remove_upper_margin}
      backgroundImage={background_image}
    >
      <div className="flex w-full flex-col items-start gap-8 p-[15px] md:flex-row md:items-center md:gap-12">
        {hasTitle && (
          <RevealOnScroll className="w-full md:w-auto md:max-w-[40%] md:shrink-0">
            <CalloutTitle title={title} />
          </RevealOnScroll>
        )}
        {hasBody && <CalloutBody body={body} delay={200} />}
      </div>
    </CalloutCardShell>
  );
};

const SplitParagraphCalloutTitleWithCtas: FC<
  SliceComponentProps<TitleWithCtasSlice>
> = ({ slice }) => {
  const {
    title,
    body,
    primary_cta_link,
    secondary_cta_link,
    background_image,
    remove_upper_margin,
  } = slice.primary;

  const hasTitle = isFilled.richText(title);
  const hasBody = isFilled.richText(body);
  const hasBackgroundImage = isFilled.image(background_image);
  const hasPrimaryCta = isFilled.link(primary_cta_link);
  const hasSecondaryCta = isFilled.link(secondary_cta_link);
  const hasCtaRow = hasPrimaryCta || hasSecondaryCta;

  // On ≤425px screens the CTA row is pushed after the body via `order-last`
  // so mobile reads title → body → action. The hook is SSR-safe (initial
  // value false, so first paint = desktop layout); sub-426 devices flip
  // once after mount. At md+ the outer is a grid and each child has an
  // explicit cell placement, so `order` has no visual effect there —
  // desktop layout is unaffected regardless of what the hook returns.
  const showCtaBelowBody = useIsMobile({ width: 425 });

  if (!hasTitle && !hasCtaRow && !hasBody && !hasBackgroundImage) {
    return null;
  }

  return (
    <CalloutCardShell
      sliceType={slice.slice_type}
      variation={slice.variation}
      removeUpperMargin={remove_upper_margin}
      backgroundImage={background_image}
    >
      <div className="flex w-full flex-col items-start gap-8 p-[15px] md:grid md:grid-cols-[55%_1fr] md:gap-x-6 md:gap-y-4">
        {hasTitle && (
          <RevealOnScroll className="w-full md:col-start-1 md:row-start-1">
            <CalloutTitle title={title} />
          </RevealOnScroll>
        )}
        {hasCtaRow && (
          <RevealOnScroll
            delay={100}
            className={cn(
              'w-full md:col-start-1 md:row-start-2',
              showCtaBelowBody && 'order-last',
            )}
          >
            <CtaEqualWidthRow>
              {hasPrimaryCta && (
                <PrimaryGreenCtaButton field={primary_cta_link} />
              )}
              {hasSecondaryCta && (
                <OutlineCtaLink field={secondary_cta_link} tone="dark" />
              )}
            </CtaEqualWidthRow>
          </RevealOnScroll>
        )}
        {hasBody && (
          <CalloutBody
            body={body}
            delay={200}
            className="md:col-start-2 md:row-start-1 md:row-span-2 md:self-center"
          />
        )}
      </div>
    </CalloutCardShell>
  );
};

const SplitParagraphCalloutLogoDescriptionCta: FC<
  SliceComponentProps<LogoDescriptionCtaSlice>
> = ({ slice }) => {
  const { logo, body, cta_link, background_image, remove_upper_margin } =
    slice.primary;

  const hasLogo = isFilled.image(logo);
  const hasBody = isFilled.richText(body);
  const hasCta = isFilled.link(cta_link);
  const hasBackgroundImage = isFilled.image(background_image);

  if (!hasLogo && !hasBody && !hasCta && !hasBackgroundImage) {
    return null;
  }

  return (
    <CalloutCardShell
      sliceType={slice.slice_type}
      variation={slice.variation}
      removeUpperMargin={remove_upper_margin}
      backgroundImage={background_image}
    >
      <div className="flex w-full flex-col items-start gap-6 p-[15px] md:flex-row md:items-center md:gap-8">
        {hasLogo && (
          <RevealOnScroll className="w-full md:w-auto md:shrink-0">
            <CalloutLogo logo={logo} className="h-[40px] md:h-[50px]" />
          </RevealOnScroll>
        )}
        {hasBody && (
          <CalloutBody body={body} delay={100} className="md:flex-1" />
        )}
        {hasCta && (
          <RevealOnScroll delay={200} className="w-full md:w-auto md:shrink-0">
            <OutlineCtaLink field={cta_link} tone="dark" />
          </RevealOnScroll>
        )}
      </div>
    </CalloutCardShell>
  );
};

const SplitParagraphCallout: FC<SplitParagraphCalloutProps> = ({
  slice,
  ...rest
}) => {
  if (slice.variation === 'titleWithCtas') {
    return <SplitParagraphCalloutTitleWithCtas slice={slice} {...rest} />;
  }

  if (slice.variation === 'logoDescriptionCta') {
    return <SplitParagraphCalloutLogoDescriptionCta slice={slice} {...rest} />;
  }

  return <SplitParagraphCalloutDefault slice={slice} {...rest} />;
};

export default SplitParagraphCallout;
